import {Box} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ArrSectionCard from '../../../../common/arr-accordion-v2/ArrSectionCard';
import AutoGrowTextField from '../auto-grow-text-field';
import QuestionReadOnlyList from '../question-read-only-list-v2/QuestionReadOnlyList';
import {
  EscalationTrigger,
  ExclusionGate,
  NegativeExclusionQuestion,
  PrimaryDecisionQuestion,
  RelevanceCriteria as RelevanceCriteriaData,
  SecondarySupportingQuestion,
  updateProtocolSection,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {RootState} from '../../../../../redux/store';
import {
  appendItem,
  createEmptyEscalationTrigger,
  createEmptyExclusionGate,
  createEmptyNegativeExclusionQuestion,
  createEmptyPrimaryDecisionQuestion,
  createEmptySecondarySupportingQuestion,
  mapRelevanceCriteriaToReadOnlySections,
  removeItem,
  updateItem,
} from './utils';
import BackToTopButton from '../back-to-top/BackToTopButton';
import './RelevanceCriteria.scss';

export type RelevanceCriteriaMode = 'edit' | 'view';

export interface RelevanceCriteriaProps {
  mode?: RelevanceCriteriaMode;
}

type CriteriaItem =
  | ExclusionGate
  | PrimaryDecisionQuestion
  | SecondarySupportingQuestion
  | NegativeExclusionQuestion
  | EscalationTrigger;

const ID_PLACEHOLDER_KEY_PREFIX =
  'ai.review.protocolWizard.relevanceCriteria.idPlaceholders';
const TOOLTIP_KEY_PREFIX =
  'ai.review.protocolWizard.relevanceCriteria.tooltips';

const QUESTION_ID_PLACEHOLDER_KEYS = {
  primary_decision_questions: `${ID_PLACEHOLDER_KEY_PREFIX}.primaryDecisionQuestions`,
  secondary_supporting_questions: `${ID_PLACEHOLDER_KEY_PREFIX}.secondarySupportingQuestions`,
  negative_exclusion_questions: `${ID_PLACEHOLDER_KEY_PREFIX}.negativeExclusionQuestions`,
  escalation_triggers: `${ID_PLACEHOLDER_KEY_PREFIX}.escalationTriggers`,
} as const;

const RelevanceCriteriaView = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const criteria = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.relevanceCriteria
  );

  return (
    <Box
      ref={scrollContainerRef}
      className="relevance-criteria relevance-criteria--view"
      data-testid="relevance-criteria"
    >
      <QuestionReadOnlyList
        sections={mapRelevanceCriteriaToReadOnlySections(criteria)}
        dataTestId="relevance-criteria-view"
      />
      <BackToTopButton scrollContainerRef={scrollContainerRef} />
    </Box>
  );
};

const RelevanceCriteriaEdit = () => {
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const criteria = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.relevanceCriteria
  );

  const updateCriteria = (data: Partial<RelevanceCriteriaData>) => {
    dispatch(updateProtocolSection({section: 'relevanceCriteria', data}));
  };

  useEffect(() => {
    const data: Partial<RelevanceCriteriaData> = {};

    if (criteria.exclusion_gates.length === 0) {
      data.exclusion_gates = [createEmptyExclusionGate()];
    }
    if (criteria.primary_decision_questions.length === 0) {
      data.primary_decision_questions = [createEmptyPrimaryDecisionQuestion()];
    }
    if (criteria.secondary_supporting_questions.length === 0) {
      data.secondary_supporting_questions = [
        createEmptySecondarySupportingQuestion(),
      ];
    }
    if (criteria.negative_exclusion_questions.length === 0) {
      data.negative_exclusion_questions = [
        createEmptyNegativeExclusionQuestion(),
      ];
    }
    if (criteria.escalation_triggers.length === 0) {
      data.escalation_triggers = [createEmptyEscalationTrigger()];
    }

    if (Object.keys(data).length > 0) {
      updateCriteria(data);
    }
  }, [
    criteria.escalation_triggers.length,
    criteria.exclusion_gates.length,
    criteria.negative_exclusion_questions.length,
    criteria.primary_decision_questions.length,
    criteria.secondary_supporting_questions.length,
  ]);

  const updateListItem = <T extends CriteriaItem>(
    field:
      | 'exclusion_gates'
      | 'primary_decision_questions'
      | 'secondary_supporting_questions'
      | 'negative_exclusion_questions'
      | 'escalation_triggers',
    items: T[],
    index: number,
    item: T
  ) => {
    updateCriteria({[field]: updateItem(items, index, item)});
  };

  const addListItem = <T extends CriteriaItem>(
    field:
      | 'exclusion_gates'
      | 'primary_decision_questions'
      | 'secondary_supporting_questions'
      | 'negative_exclusion_questions'
      | 'escalation_triggers',
    items: T[],
    emptyItem: T
  ) => {
    updateCriteria({[field]: appendItem(items, emptyItem)});
  };

  const deleteListItem = <T extends CriteriaItem>(
    field:
      | 'exclusion_gates'
      | 'primary_decision_questions'
      | 'secondary_supporting_questions'
      | 'negative_exclusion_questions'
      | 'escalation_triggers',
    items: T[],
    index: number,
    emptyItem: T
  ) => {
    updateCriteria({
      [field]: items.length > 1 ? removeItem(items, index) : [emptyItem],
    });
  };

  const renderField = (
    value: string,
    placeholder: string,
    dataTestId: string,
    onChange: (value: string) => void,
    multiline = true
  ) => (
    <AutoGrowTextField
      className="relevance-criteria__field"
      placeholder={placeholder}
      value={value}
      multiline={multiline}
      onChange={onChange}
      dataTestId={dataTestId}
    />
  );

  const renderExclusionGate = (item: ExclusionGate, index: number) => (
    <>
      <ArrSectionCard.Row className="relevance-criteria__row">
        <ArrSectionCard.Item flex={1} minWidth={0}>
          {renderField(
            item.id,
            t(`${ID_PLACEHOLDER_KEY_PREFIX}.exclusionGates`),
            `relevance-criteria-exclusion-gate-id-${index}`,
            (value) =>
              updateListItem(
                'exclusion_gates',
                criteria.exclusion_gates,
                index,
                {
                  ...item,
                  id: value,
                }
              )
          )}
        </ArrSectionCard.Item>
        <ArrSectionCard.Item flex={1} minWidth={0}>
          {renderField(
            item.other,
            t('ai.review.protocolWizard.relevanceCriteria.fields.other'),
            `relevance-criteria-exclusion-gate-other-${index}`,
            (value) =>
              updateListItem(
                'exclusion_gates',
                criteria.exclusion_gates,
                index,
                {
                  ...item,
                  other: value,
                }
              )
          )}
        </ArrSectionCard.Item>
      </ArrSectionCard.Row>
      <Box className="relevance-criteria__stacked-fields">
        {renderField(
          item.hard_rule,
          t('ai.review.protocolWizard.relevanceCriteria.fields.hardRule'),
          `relevance-criteria-exclusion-gate-rule-${index}`,
          (value) =>
            updateListItem('exclusion_gates', criteria.exclusion_gates, index, {
              ...item,
              hard_rule: value,
            })
        )}
        {renderField(
          item.tag,
          t('ai.review.protocolWizard.relevanceCriteria.fields.tag'),
          `relevance-criteria-exclusion-gate-tag-${index}`,
          (value) =>
            updateListItem('exclusion_gates', criteria.exclusion_gates, index, {
              ...item,
              tag: value,
            })
        )}
      </Box>
    </>
  );

  const renderQuestion = (
    item:
      | PrimaryDecisionQuestion
      | SecondarySupportingQuestion
      | NegativeExclusionQuestion
      | EscalationTrigger,
    index: number,
    field:
      | 'primary_decision_questions'
      | 'secondary_supporting_questions'
      | 'negative_exclusion_questions'
      | 'escalation_triggers',
    items: (
      | PrimaryDecisionQuestion
      | SecondarySupportingQuestion
      | NegativeExclusionQuestion
      | EscalationTrigger
    )[],
    emptyItem: CriteriaItem,
    secondField: 'rationale' | 'purpose',
    secondFieldLabel = secondField
  ) => (
    <ArrSectionCard.Row className="relevance-criteria__row">
      <ArrSectionCard.Item width={120} flex={0} minWidth={120} maxWidth={120}>
        {renderField(
          item.id,
          t(QUESTION_ID_PLACEHOLDER_KEYS[field]),
          `relevance-criteria-${field}-id-${index}`,
          (value) =>
            updateListItem(field, items, index, {...item, id: value} as never)
        )}
      </ArrSectionCard.Item>
      <ArrSectionCard.Item
        className="relevance-criteria__stacked-fields"
        flex={1}
        minWidth={0}
      >
        {renderField(
          item.question_text,
          t('ai.review.protocolWizard.relevanceCriteria.fields.questionText'),
          `relevance-criteria-${field}-question-${index}`,
          (value) =>
            updateListItem(field, items, index, {
              ...item,
              question_text: value,
            } as never)
        )}
        {renderField(
          'rationale' in item ? item.rationale : item.purpose,
          t(
            `ai.review.protocolWizard.relevanceCriteria.fields.${secondFieldLabel}`
          ),
          `relevance-criteria-${field}-${secondField}-${index}`,
          (value) =>
            updateListItem(field, items, index, {
              ...item,
              [secondField]: value,
            } as never)
        )}
      </ArrSectionCard.Item>
    </ArrSectionCard.Row>
  );

  const renderCard = <T extends CriteriaItem>(
    label: string,
    dataTestId: string,
    items: T[],
    renderItem: (item: T, index: number) => React.ReactNode,
    emptyItem: T,
    field:
      | 'exclusion_gates'
      | 'primary_decision_questions'
      | 'secondary_supporting_questions'
      | 'negative_exclusion_questions'
      | 'escalation_triggers',
    tooltipKey: string
  ) => (
    <ArrSectionCard
      accordion
      label={label}
      showInfoIcon
      infoContent={t(tooltipKey)}
      items={items}
      renderItem={renderItem}
      showAddButton
      showDeleteButton={items.length > 1}
      addButtonLabel={t('common.add')}
      onAdd={() => addListItem(field as never, items, emptyItem)}
      onDelete={(index) =>
        deleteListItem(field as never, items, index, emptyItem)
      }
      className="relevance-criteria__section"
      dataTestId={dataTestId}
    />
  );

  return (
    <Box
      ref={scrollContainerRef}
      className="relevance-criteria"
      data-testid="relevance-criteria"
      role="region"
      aria-label={t('ai.review.newJobDetail.relevanceCriteria')}
    >
      <ArrSectionCard
        accordion
        label={
          <span>
            {t(
              'ai.review.protocolWizard.relevanceCriteria.sections.masterStandard'
            )}
            <span
              className="relevance-criteria__required-marker typo-text subhead4_size"
              aria-label="required"
            >
              *
            </span>
          </span>
        }
        showInfoIcon
        infoContent={t(`${TOOLTIP_KEY_PREFIX}.masterStandard`)}
        items={[criteria.master_relevance_standard]}
        renderItem={() =>
          renderField(
            criteria.master_relevance_standard,
            t(
              'ai.review.protocolWizard.relevanceCriteria.fields.masterStandard'
            ),
            'relevance-criteria-master-standard',
            (value) => updateCriteria({master_relevance_standard: value})
          )
        }
        showAddButton={false}
        showDeleteButton={false}
        className="relevance-criteria__master-standard-section"
        dataTestId="relevance-criteria-master-standard-section"
      />
      {renderCard(
        t('ai.review.protocolWizard.relevanceCriteria.sections.exclusionGates'),
        'relevance-criteria-exclusion-gates',
        criteria.exclusion_gates,
        renderExclusionGate,
        createEmptyExclusionGate(),
        'exclusion_gates',
        `${TOOLTIP_KEY_PREFIX}.exclusionGates`
      )}
      {renderCard(
        t(
          'ai.review.protocolWizard.relevanceCriteria.sections.primaryDecisionQuestions'
        ),
        'relevance-criteria-primary-decision-questions',
        criteria.primary_decision_questions,
        (item, index) =>
          renderQuestion(
            item,
            index,
            'primary_decision_questions',
            criteria.primary_decision_questions,
            createEmptyPrimaryDecisionQuestion(),
            'rationale'
          ),
        createEmptyPrimaryDecisionQuestion(),
        'primary_decision_questions',
        `${TOOLTIP_KEY_PREFIX}.primaryDecisionQuestions`
      )}
      {renderCard(
        t(
          'ai.review.protocolWizard.relevanceCriteria.sections.secondarySupportingQuestions'
        ),
        'relevance-criteria-secondary-supporting-questions',
        criteria.secondary_supporting_questions,
        (item, index) =>
          renderQuestion(
            item,
            index,
            'secondary_supporting_questions',
            criteria.secondary_supporting_questions,
            createEmptySecondarySupportingQuestion(),
            'purpose'
          ),
        createEmptySecondarySupportingQuestion(),
        'secondary_supporting_questions',
        `${TOOLTIP_KEY_PREFIX}.secondarySupportingQuestions`
      )}
      {renderCard(
        t(
          'ai.review.protocolWizard.relevanceCriteria.sections.negativeExclusionQuestions'
        ),
        'relevance-criteria-negative-exclusion-questions',
        criteria.negative_exclusion_questions,
        (item, index) =>
          renderQuestion(
            item,
            index,
            'negative_exclusion_questions',
            criteria.negative_exclusion_questions,
            createEmptyNegativeExclusionQuestion(),
            'purpose'
          ),
        createEmptyNegativeExclusionQuestion(),
        'negative_exclusion_questions',
        `${TOOLTIP_KEY_PREFIX}.negativeExclusionQuestions`
      )}
      {renderCard(
        t(
          'ai.review.protocolWizard.relevanceCriteria.sections.escalationTriggers'
        ),
        'relevance-criteria-escalation-triggers',
        criteria.escalation_triggers,
        (item, index) =>
          renderQuestion(
            item,
            index,
            'escalation_triggers',
            criteria.escalation_triggers,
            createEmptyEscalationTrigger(),
            'purpose',
            'escalationReason'
          ),
        createEmptyEscalationTrigger(),
        'escalation_triggers',
        `${TOOLTIP_KEY_PREFIX}.escalationTriggers`
      )}
      <BackToTopButton scrollContainerRef={scrollContainerRef} />
    </Box>
  );
};

const RelevanceCriteria = ({mode = 'edit'}: RelevanceCriteriaProps) => {
  if (mode === 'view') {
    return <RelevanceCriteriaView />;
  }

  return <RelevanceCriteriaEdit />;
};

export default RelevanceCriteria;
